import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Banknote, CreditCard, Check, Bike, Footprints, MapPin } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { MedicineEntry } from "@/components/MedicineEntry";
import { AddressPicker } from "@/components/AddressPicker";

const TEAL = "#005C66";

export default function PlaceOrder() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id || user?.id || user?.userId;
  const pharmacyId = sp.get("pharmacyId");
  const pharmacyName = sp.get("pharmacyName") || "Pharmacy";
  const conversationId = sp.get("conversationId");

  const [pharmacy, setPharmacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({ items: [], hasRx: false, selectedRx: null });
  const [deliveryMode, setDeliveryMode] = useState("pickup");
  const [address, setAddress] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onMedChange = useCallback((v) => setSelection(v), []);

  useEffect(() => {
    (async () => {
      try {
        if (pharmacyId) {
          const res = await api.get(`/patient/pharmacies/${pharmacyId}`);
          if (res.data?.success) setPharmacy(res.data.pharmacy);
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [pharmacyId]);

  const submit = async () => {
    setError("");
    if (!selection.items.length) { setError("Add at least one medicine."); return; }
    if (selection.hasRx && !selection.selectedRx) { setError("A prescription is required for Schedule-H medicines. Select or upload one."); return; }
    if (deliveryMode === "delivery" && !address) { setError("Add your delivery address."); return; }
    setSubmitting(true);
    try {
      const fullAddr = address ? [address.exactAddress, address.formattedAddress].filter(Boolean).join(", ") : null;
      const res = await api.post(`/patient/${userId}/medicine-orders`, {
        pharmacy_id: pharmacyId,
        pharmacy_name: pharmacyName || pharmacy?.name,
        items: selection.items,
        has_rx_items: selection.hasRx,
        prescription_document_id: selection.hasRx ? selection.selectedRx : null,
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === "delivery" ? fullAddr : null,
        delivery_address_id: address?.addressId || null,
        delivery_lat: address?.lat, delivery_lng: address?.lng,
        payment_method: "cash",
        conversation_id: conversationId || null,
        notes: notes.trim(),
      });
      if (res.data?.success) navigate("/medications?tab=orders");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto p-10 text-center text-muted-foreground">Loading…</div>;
  const canDeliver = pharmacy?.homeDelivery;
  const deliveryRadius = pharmacy?.deliveryRadiusKm;

  return (
    <div className="max-w-2xl mx-auto px-4 pb-28">
      <div className="flex items-center gap-2 py-4 sticky top-0 bg-background z-10">
        <button onClick={() => navigate(-1)} data-testid="order-back" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent"><ArrowLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="text-2xl font-semibold">Place Order</h1>
          <p className="text-sm text-muted-foreground">{pharmacyName}</p>
        </div>
      </div>

      <MedicineEntry userId={userId} onChange={onMedChange} />

      <h2 className="font-bold mt-5 mb-2">Fulfilment</h2>
      <div className="flex gap-3">
        <button data-testid="order-pickup" onClick={() => setDeliveryMode("pickup")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${deliveryMode === "pickup" ? "text-white" : ""}`} style={deliveryMode === "pickup" ? { backgroundColor: TEAL, borderColor: TEAL } : { borderColor: "#D1E7E9" }}>
          <Footprints className="h-4 w-4" /> Pickup
        </button>
        <button data-testid="order-delivery" disabled={!canDeliver} onClick={() => canDeliver && setDeliveryMode("delivery")} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border ${deliveryMode === "delivery" ? "text-white" : ""} ${!canDeliver ? "opacity-50" : ""}`} style={deliveryMode === "delivery" ? { backgroundColor: TEAL, borderColor: TEAL } : { borderColor: "#D1E7E9" }}>
          <Bike className="h-4 w-4" /> Delivery{canDeliver && deliveryRadius ? ` (≤${deliveryRadius}km)` : ""}
        </button>
      </div>
      {!canDeliver && <p className="text-xs text-muted-foreground mt-1">This pharmacy offers pickup only.</p>}

      {deliveryMode === "delivery" && (
        <div className="mt-3">
          {address ? (
            <Card className="rounded-2xl p-3 flex items-start gap-2" data-testid="order-address-confirmed">
              <MapPin className="h-4 w-4 mt-0.5" style={{ color: TEAL }} />
              <p className="text-sm flex-1">{address.exactAddress ? `${address.exactAddress}, ` : ""}{address.formattedAddress}</p>
              <button onClick={() => setAddress(null)} className="text-xs font-semibold" style={{ color: TEAL }} data-testid="order-address-change">Change</button>
            </Card>
          ) : (
            <AddressPicker userId={userId} onSelect={setAddress} />
          )}
        </div>
      )}
      <Textarea data-testid="order-notes" className="mt-3" placeholder="Notes for the pharmacy (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

      {/* Order summary */}
      {selection.items.length > 0 && (
        <Card className="rounded-2xl p-4 mt-5" data-testid="order-summary">
          <h2 className="font-bold mb-2">Order summary</h2>
          <div className="space-y-1">
            {selection.items.map((it, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="truncate">{it.name}</span>
                <span className="text-muted-foreground">{it.quantity || "1"}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm font-semibold pt-1 border-t mt-1">
              <span>{selection.items.length} item{selection.items.length === 1 ? "" : "s"}</span>
              <span>{pharmacyName}</span>
            </div>
          </div>
        </Card>
      )}

      <h2 className="font-bold mt-5 mb-2">Payment</h2>
      <div className="space-y-2">
        <div data-testid="order-pay-cash" className="flex items-center gap-3 py-3 px-4 rounded-xl border" style={{ borderColor: TEAL, backgroundColor: "#F0F9FA" }}>
          <Banknote className="h-5 w-5" style={{ color: TEAL }} /> <span className="font-semibold">Cash</span>
          <Check className="h-5 w-5 ml-auto" style={{ color: TEAL }} />
        </div>
        <div data-testid="order-pay-online" className="flex items-center gap-3 py-3 px-4 rounded-xl border opacity-70" style={{ borderColor: "#E2E8F0" }}>
          <CreditCard className="h-5 w-5 text-muted-foreground" /> <span className="font-semibold text-muted-foreground">Pay online</span>
          <span className="ml-auto text-xs font-bold text-muted-foreground bg-muted px-2 py-1 rounded-lg">Coming soon</span>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mt-4 font-semibold" data-testid="order-error">{error}</p>}

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t">
        <div className="max-w-2xl mx-auto">
          <Button data-testid="order-submit" onClick={submit} disabled={submitting} className="w-full rounded-xl py-6 text-base" style={{ backgroundColor: TEAL }}>
            {submitting ? "Sending…" : "Send order request"}
          </Button>
        </div>
      </div>
    </div>
  );
}
